import React, {useState} from 'react';

import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  SampleStatusWarningQuery,
  SampleStatusWarningQueryVariables,
} from 'inventory/sample-status/__generated/SampleStatusWarningQuery';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  doesCurrentUserHaveAccessForItem,
  isItemAvailableToCurrentUser,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {SAMPLE_STATUS_WARNING_QUERY, SampleStatusWarning} from 'inventory/sample-status/SampleStatusWarning';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {ButtonStyle} from 'ui-library/buttons/Button';
import ErrorMessage from 'ui-library/ErrorMessage';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

type SampleStatusStorable = SampleStatusWarningQuery['storables'][number];

export interface SampleStatusWarningResult {
  shouldProceed: boolean;
  selectedSampleIds: Array<string>;
}

export interface SampleStatusWarningModalProps {
  sampleIds: Array<string>;
  onRequestSubmit: (warningResult: SampleStatusWarningResult) => void;
  onRequestClose: () => void;
  trackingParams: BaseSampleStatusWarningTrackingParams;
  canSelectMultiple?: boolean;
  showOnlySamplesWithWarnings?: boolean;
}

interface QueriedProps {
  samples: Array<SampleStatusItem>;
}

export async function callSampleStatusWarningModal({
  sampleIds,
  trackingParams,
  canSelectMultiple,
  showOnlyWarnings,
}: {
  sampleIds: Array<string>;
  trackingParams: BaseSampleStatusWarningTrackingParams;
  canSelectMultiple?: boolean;
  showOnlyWarnings?: boolean;
}): Promise<SampleStatusWarningResult> {
  const result = await legacyRunModal(QueryingSampleStatusWarningModal, {
    sampleIds,
    trackingParams,
    canSelectMultiple,
    showOnlySamplesWithWarnings: showOnlyWarnings,
  });
  if (result == null) {
    return {shouldProceed: false, selectedSampleIds: []};
  }
  return result;
}

export function SampleStatusWarningModal(props: SampleStatusWarningModalProps & QueriedProps): JSX.Element {
  const [canProceedOnSampleStatus, setCanProceedOnSampleStatus] = useState(false);
  const [selectedSampleIds, setSelectedSampleIds] = useState<Array<string>>(props.sampleIds);
  const [error, setError] = useState<string | null>(null);

  const validateSelectedSampleIds = (): boolean => {
    if (!selectedSampleIds.length) {
      setError('Please select at least one sample.');
      return false;
    } else {
      setError(null);
      return true;
    }
  };

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Cancel',
          submit: false,
          onClick: () => {
            props.onRequestSubmit({shouldProceed: false, selectedSampleIds: []});
          },
        },
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Submit',
          disabled: !canProceedOnSampleStatus,
        },
      ]}
      onRequestClose={() => {
        props.onRequestSubmit({shouldProceed: false, selectedSampleIds: []});
      }}
      onRequestSubmit={() => {
        if (validateSelectedSampleIds()) {
          props.onRequestSubmit({shouldProceed: true, selectedSampleIds});
        }
      }}
      title="Verify that you can use these items"
    >
      <SampleStatusWarning
        sampleIds={props.sampleIds}
        shouldProceed={canProceedOnSampleStatus}
        setShouldProceed={setCanProceedOnSampleStatus}
        selectedSampleIds={selectedSampleIds}
        setSelectedSampleIds={setSelectedSampleIds}
        samples={props.samples}
        trackingParams={props.trackingParams}
        canSelectMultiple={props.canSelectMultiple}
      />
      {error != null && <ErrorMessage error={error} />}
    </Modal>
  );
}

function QueryingSampleStatusWarningModal(props: SampleStatusWarningModalProps): JSX.Element | null {
  const storableIds = props.sampleIds.filter((id) => APIUtil.isStorable(id));
  const result = useSkippableBenchlingQuery<SampleStatusWarningQuery, SampleStatusWarningQueryVariables>(
    SAMPLE_STATUS_WARNING_QUERY,
    {
      variables: {sampleIds: storableIds},
      skip: !hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1),
    }
  );

  switch (result.type) {
    case QueryResultType.SKIPPED: {
      props.onRequestSubmit({shouldProceed: true, selectedSampleIds: props.sampleIds});
      return null;
    }
    case QueryResultType.PENDING:
      return <CenteredHorizontalThrobber />;
    case QueryResultType.SUCCESS: {
      const samples: Array<SampleStatusItem> = result.data.storables.flatMap((s: SampleStatusStorable) =>
        isInaccessible(s) ? [] : [s]
      );
      const samplesWithWarnings = samples.filter(
        (sample) => !isItemAvailableToCurrentUser(sample) || !doesCurrentUserHaveAccessForItem(sample)
      );

      if (samplesWithWarnings.length > 0) {
        // eslint-disable-next-line benchlint/no-spread-reduce
        const samplesById = samples.reduce(
          (byId: {[sampleId: string]: SampleStatusItem}, sample) => ({...byId, [sample.id]: sample}),
          {}
        );

        const sampleIds = props.showOnlySamplesWithWarnings
          ? samplesWithWarnings.map((sample) => sample.id)
          : props.sampleIds;
        const allSamples = sampleIds.map((sampleId) => samplesById[sampleId] ?? {id: sampleId});
        return <SampleStatusWarningModal {...props} sampleIds={sampleIds} samples={allSamples} />;
      } else {
        props.onRequestSubmit({shouldProceed: true, selectedSampleIds: props.sampleIds});
        return null;
      }
    }
    default:
      return null;
  }
}

export default QueryingSampleStatusWarningModal;
